import { usePeopleTableColumns } from 'components/People/Table';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTableSortableHeaderCellEffectHandlers } from 'shell/components';
import { buildPeopleVisibleRows } from 'utils/chat/instructorHugoContext';
import { downloadCSV, getPrettySections, getUserPreferredFullName } from 'utils/helper';

const handleFilters = (items, filters, setItems) => {
    const STAFF_ROLES = ['instructor', 'ta'];
    const search = filters.search.toLowerCase();

    const res = items.filter((item) => {
        const matchesSearch = getUserPreferredFullName(item).toLowerCase().includes(search);

        const matchesRole =
            filters.roles.length === 0 ||
            (filters.roles.includes('staff') && STAFF_ROLES.includes(item.role)) ||
            (filters.roles.includes('student') && item.role === 'student');

        return matchesSearch && matchesRole;
    });

    setItems(res);
};

const PageContext = createContext();
const ACTIVE_ROSTER_SYNC_STATUSES = ['queued', 'running'];
const MAX_ROSTER_SYNC_ACTIVE_MS = 30 * 60 * 1000;
const PENDING_AUTO_ROSTER_SYNC_KEY = 'pending_auto_roster_sync';

const isRosterSyncStale = (course) => {
    if (!ACTIVE_ROSTER_SYNC_STATUSES.includes(course?.roster_sync_status)) return false;
    const lastActiveOn = course.roster_sync_heartbeat_on || course.roster_sync_started_on;
    if (!lastActiveOn) return false;
    return Date.now() - lastActiveOn > MAX_ROSTER_SYNC_ACTIVE_MS;
};

const PageProvider = (props) => {
    const {
        course,
        accountUsers,
        setAccountUsers,
        studentAccountUsers,
        changeAccountUserRole,
        setInstructorHugoVisibleRows,
    } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation();
    const columns = usePeopleTableColumns();

    const [filters, setFilters] = useState({
        open: false,
        roles: [],
        search: '',
    });
    const [checked, setChecked] = useState([]);
    const [syncRosterDialog, setSyncRosterDialog] = useState({ open: false });
    const actualRosterSyncStatus = isRosterSyncStale(course) ? null : course?.roster_sync_status || null;
    const rosterSyncError = course?.roster_sync_error || null;
    const rosterSyncProgress = course?.roster_sync_progress || null;
    const pendingAutoRosterSync = (() => {
        try {
            const value = sessionStorage.getItem(PENDING_AUTO_ROSTER_SYNC_KEY);
            return value ? JSON.parse(value) : null;
        } catch (error) {
            return null;
        }
    })();
    const hasPendingAutoRosterSync = pendingAutoRosterSync?.courseId === `${course?.id || ''}`;
    const pendingAutoRosterSyncTriggeredOn = Number(pendingAutoRosterSync?.triggeredOn || 0);
    const isPendingAutoRosterSyncFresh =
        pendingAutoRosterSyncTriggeredOn && Date.now() - pendingAutoRosterSyncTriggeredOn <= MAX_ROSTER_SYNC_ACTIVE_MS;
    const shouldShowPendingAutoRosterSync = hasPendingAutoRosterSync && isPendingAutoRosterSyncFresh;
    const rosterSyncStatus = actualRosterSyncStatus || (shouldShowPendingAutoRosterSync ? 'queued' : null);
    const isRosterSyncing = ACTIVE_ROSTER_SYNC_STATUSES.includes(rosterSyncStatus);

    useEffect(() => {
        if (!course?.id || !hasPendingAutoRosterSync) return;
        if (!isPendingAutoRosterSyncFresh) {
            sessionStorage.removeItem(PENDING_AUTO_ROSTER_SYNC_KEY);
            return;
        }

        if (
            course?.roster_sync_finished_on &&
            pendingAutoRosterSyncTriggeredOn &&
            course.roster_sync_finished_on >= pendingAutoRosterSyncTriggeredOn
        ) {
            sessionStorage.removeItem(PENDING_AUTO_ROSTER_SYNC_KEY);
        }
    }, [
        actualRosterSyncStatus,
        course?.id,
        course?.roster_sync_finished_on,
        hasPendingAutoRosterSync,
        isPendingAutoRosterSyncFresh,
        pendingAutoRosterSyncTriggeredOn,
    ]);

    const {
        sortedFilteredData: sortedFilteredAccountUsers,
        sortState,
        handleSetSortState,
    } = useTableSortableHeaderCellEffectHandlers(accountUsers, columns, filters, setAccountUsers, handleFilters);

    const hugoVisibleRows = buildPeopleVisibleRows(sortedFilteredAccountUsers || []);
    const hugoVisibleRowsKey = JSON.stringify(hugoVisibleRows || null);

    useEffect(() => {
        setInstructorHugoVisibleRows(hugoVisibleRows);
        return () => setInstructorHugoVisibleRows(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hugoVisibleRowsKey, setInstructorHugoVisibleRows]);

    const resetFilters = useCallback(() => {
        setFilters((current) => ({
            ...current,
            roles: [],
        }));
        handleSetSortState({ order: 'asc', orderBy: null });
    }, [handleSetSortState]);

    const getFilterCount = (filters, sortState) => {
        let count = 0;
        if (sortState.orderBy) count++;
        if (filters.roles.length) count++;
        return count;
    };

    const downloadCourseRoster = useCallback(() => {
        const isLti = process.env.REACT_APP_AUTH === 'LTI';
        const data = (studentAccountUsers || []).map((student) => {
            return [
                student.display_name || student.first_name || '',
                student.last_name || '',
                isLti ? student.username : student.email,
                getPrettySections(student.sections),
            ];
        });
        return downloadCSV(`${course.name}_roster_template.csv`.toLowerCase().replaceAll(' ', '_'), [
            [
                t('table.first-name', 'First Name'),
                t('table.last-name', 'Last Name'),
                t('table.email', 'Email'),
                t('table.section-s', 'Section(s)'),
            ],
            ...data,
        ]);
    }, [course?.name, studentAccountUsers, t]);

    const getOptions = useCallback(
        (accountUser) => {
            return [
                { key: 'copy-email', value: t('people.copy-email', 'Copy Email') },
                ...(accountUser.owner_account_id !== auth.owner_account_id && accountUser.role === 'instructor'
                    ? [{ key: 'update-role-to-ta', value: t('people.change-to-ta', 'Change to TA') }]
                    : []),
                ...(accountUser.owner_account_id !== auth.owner_account_id && accountUser.role === 'ta'
                    ? [
                          {
                              key: 'update-role-to-instructor',
                              value: t('people.change-to-instructor', 'Change to Instructor'),
                          },
                      ]
                    : []),
            ];
        },
        [auth.owner_account_id, t]
    );

    const handleSelect = useCallback(
        async ({ key }, accountUser) => {
            if (key === 'copy-email') {
                await navigator.clipboard.writeText(accountUser.email);
                message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
            }
            if (key === 'update-role-to-ta' || key === 'update-role-to-instructor') {
                setDialog({
                    open: true,
                    message: t('dialog.change-role.title', 'Confirm Role Change?'),
                    description: t(
                        'dialog.change-role.description',
                        'Visibility is role-based. Instructors and TAs only see what their permissions allow.'
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('controls.confirm', 'Confirm'),
                    cancelButtonText: t('controls.cancel', 'Cancel'),
                    onAction: () => {
                        closeDialog();
                        changeAccountUserRole(accountUser, key === 'update-role-to-ta' ? 'ta' : 'instructor');
                    },
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }
        },
        [changeAccountUserRole, closeDialog, message, setDialog, t]
    );

    const value = useMemo(
        () => ({
            accountUsers: sortedFilteredAccountUsers,
            sortState,
            handleSetSortState,
            filters,
            setFilters,
            resetFilters,
            getFilterCount,
            checked,
            setChecked,
            rosterSyncStatus,
            rosterSyncError,
            rosterSyncProgress,
            isRosterSyncing,
            hasPendingAutoRosterSync,
            downloadCourseRoster,
            columns,
            syncRosterDialog,
            setSyncRosterDialog,
            getOptions,
            handleSelect,
        }),
        [
            checked,
            downloadCourseRoster,
            filters,
            getOptions,
            handleSelect,
            handleSetSortState,
            isRosterSyncing,
            hasPendingAutoRosterSync,
            resetFilters,
            rosterSyncError,
            rosterSyncProgress,
            rosterSyncStatus,
            sortState,
            sortedFilteredAccountUsers,
            columns,
            syncRosterDialog,
        ]
    );

    return <PageContext.Provider value={value} {...props} />;
};

export { PageContext };
export default PageProvider;
